/**
 * How long a sign-in lasts.
 *
 * - 8 hours without any activity (click, key, scroll, touch) in any tab ends
 *   the session. A warning with "Stay signed in" comes a minute before.
 * - 12 hours after signing in the session ends regardless; a warning comes
 *   five minutes before.
 *
 * Supabase would otherwise refresh the token forever, which kept a browser
 * signed in for days. The same limits are set in Supabase (Authentication >
 * Sessions: time-box 12h, inactivity 8h) so a copied token cannot outlive them.
 *
 * The last activity is shared between tabs through localStorage, so working in
 * one tab keeps the others signed in. It is checked before the app starts
 * (src/sessionBoot.ts), so a laptop woken after a weekend opens signed out
 * instead of showing the app for a moment.
 */

export const IDLE_LIMIT_MS = 8 * 60 * 60 * 1000;
export const MAX_SESSION_MS = 12 * 60 * 60 * 1000;
export const IDLE_WARNING_MS = 60 * 1000;
export const MAX_WARNING_MS = 5 * 60 * 1000;

export const ACTIVITY_KEY = "ec_last_activity";
/** Why the last session ended, for the notice on the sign-in page (this tab only). */
export const SIGNED_OUT_KEY = "ec_signed_out_reason";

// supabase-js stores the session under sb-<project ref>-auth-token.
const AUTH_KEY = "sb-scluxxccawjbmeelfdwr-auth-token";

export type ExpiryReason = "idle" | "expired";

export function readLastActivity(): number | null {
  try {
    const v = Number(localStorage.getItem(ACTIVITY_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

export function markActivity(now = Date.now()): void {
  try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch { /* storage blocked */ }
}

export function clearActivity(): void {
  try { localStorage.removeItem(ACTIVITY_KEY); } catch { /* storage blocked */ }
}

/** When the user signed in, from the session's user record. */
export function signedInAt(user: { last_sign_in_at?: string | null } | null | undefined): number | null {
  const t = Date.parse(user?.last_sign_in_at ?? "");
  return Number.isFinite(t) ? t : null;
}

export interface SessionClock {
  reason: ExpiryReason;
  /** Milliseconds until the session ends (0 or less: it has ended). */
  remaining: number;
}

/** Which limit ends the session first, and when. */
export function sessionClock(lastActivity: number | null, signIn: number | null, now = Date.now()): SessionClock {
  const idleLeft = lastActivity == null ? IDLE_LIMIT_MS : lastActivity + IDLE_LIMIT_MS - now;
  const maxLeft = signIn == null ? Infinity : signIn + MAX_SESSION_MS - now;
  return maxLeft <= idleLeft ? { reason: "expired", remaining: maxLeft } : { reason: "idle", remaining: idleLeft };
}

/** Local data that belongs to the signed-in user and must not outlive the session. */
export function clearUserLocalData(): void {
  try {
    const drop: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith("cro-audit-draft") || k === "selectedPlan" || k === "appliedDiscount")) drop.push(k);
    }
    drop.forEach((k) => localStorage.removeItem(k));
  } catch { /* storage blocked */ }
}

export function rememberSignOutReason(reason: ExpiryReason): void {
  try { sessionStorage.setItem(SIGNED_OUT_KEY, reason); } catch { /* storage blocked */ }
}

/** Read once: the sign-in page shows why, then forgets. */
export function takeSignOutReason(): ExpiryReason | null {
  try {
    const r = sessionStorage.getItem(SIGNED_OUT_KEY);
    sessionStorage.removeItem(SIGNED_OUT_KEY);
    return r === "idle" || r === "expired" ? r : null;
  } catch {
    return null;
  }
}

/**
 * Runs before the Supabase client reads its storage. A stored session past
 * either limit is dropped, so the app starts signed out. Supabase's own
 * session limits revoke the refresh token on the server as well.
 */
export function expireStaleSessionOnBoot(now = Date.now()): ExpiryReason | null {
  if (typeof window === "undefined") return null;
  let raw: string | null = null;
  try { raw = localStorage.getItem(AUTH_KEY); } catch { return null; }
  if (!raw) return null;
  let user: { last_sign_in_at?: string | null } | undefined;
  try {
    const parsed = JSON.parse(raw);
    user = parsed?.user ?? parsed?.currentSession?.user;
  } catch {
    return null;
  }
  const last = readLastActivity();
  // Signed in before these limits existed: start the inactivity clock now.
  if (last == null) markActivity(now);
  const { reason, remaining } = sessionClock(last ?? now, signedInAt(user), now);
  if (remaining > 0) return null;
  try {
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(`${AUTH_KEY}-code-verifier`);
  } catch { /* storage blocked */ }
  clearActivity();
  clearUserLocalData();
  rememberSignOutReason(reason);
  return reason;
}
